import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService } from '../services/subscription.service';
import { resultsService, type ScoreHistoryEntry, type PerformanceSummary } from '../services/results.service';
import type { Subscription } from '../services/subscription.service';
import { ProgressChart } from '../components/dashboard/ProgressChart';
import { StatsCards } from '../components/dashboard/StatsCards';
import { RecentExamsTable } from '../components/dashboard/RecentExamsTable';
import { SubscriptionLockedState } from '../components/shared/SubscriptionLockedState';
import { EmailVerificationPanel } from '../components/shared/EmailVerificationPanel';
import { Loader2, BookOpen, Headphones, PenSquare, Mic, Landmark, User, CreditCard, LogOut } from 'lucide-react';
import { getPlanLabel } from '../utils/planLabels';
import { getPassTier, isPassesModel } from '../config/pricing';

interface ExamResult {
  id: string;
  date: string;
  section: string;
  score: number;
  passed: boolean;
  durationMinutes: number;
}

function mapHistory(entries: ScoreHistoryEntry[], tFn: (key: string) => string): ExamResult[] {
  const typeToSection: Record<string, string> = {
    reading: tFn('dashboard.sections.reading.title'),
    listening: tFn('dashboard.sections.listening.title'),
    writing: tFn('dashboard.sections.writing.title'),
    speaking: tFn('dashboard.sections.speaking.title'),
    knm: tFn('dashboard.sections.knm.title'),
  };
  return entries.map((e) => ({
    id: e.attemptId,
    date: e.date,
    section: typeToSection[e.exam?.type] || e.exam?.type || tFn('dashboard.sections.reading.title'),
    score: Math.round(e.percentage ?? 0),
    passed: e.passed,
    durationMinutes: e.duration ? Math.round(e.duration / 60) : 0,
  }));
}

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [scoreHistory, setScoreHistory] = useState<ScoreHistoryEntry[]>([]);
  const [loadingResults, setLoadingResults] = useState(true);

  const isSubscribed = subscription?.status === 'active';
  const hasAccess = isSubscribed || subscription?.passActive === true;
  const emailVerificationBlocked = user?.emailVerified === false && hasAccess;

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const data = await subscriptionService.getStatus();
        setSubscription(data);
      } catch (error) {
        console.error('Failed to load subscription:', error);
      } finally {
        setLoadingSub(false);
      }
    };

    loadSubscription();
  }, []);

  useEffect(() => {
    if (loadingSub) return;

    if (!hasAccess) {
      setLoadingResults(false);
      return;
    }

    const loadResults = async () => {
      try {
        const [summaryData, historyData] = await Promise.all([
          resultsService.getPerformanceSummary(),
          resultsService.getScoreHistory(),
        ]);
        setSummary(summaryData);
        setScoreHistory(historyData);
      } catch (error) {
        console.error('Failed to load results:', error);
      } finally {
        setLoadingResults(false);
      }
    };

    loadResults();
  }, [loadingSub, hasAccess]);

  const statsForCards = useMemo(() => {
    const totalAttempts = summary?.totalAttempts ?? 0;
    const avgScore = Math.round(summary?.averageScore ?? 0);
    const streakDays = user?.statistics?.currentStreak ?? user?.statistics?.studyStreak ?? 0;
    const today = new Date().toISOString().slice(0, 10);
    const studiedToday = scoreHistory.some(
      (h: any) => h.date?.slice(0, 10) === today || h.createdAt?.slice(0, 10) === today,
    );
    return {
      totalExams: totalAttempts,
      streakDays,
      avgScore,
      studiedToday,
    };
  }, [summary, user?.statistics?.currentStreak, user?.statistics?.studyStreak, scoreHistory]);

  const recentExams = useMemo(() => mapHistory(scoreHistory, t).slice(0, 10), [scoreHistory, t]);

  if (authLoading || !user) {
    return (
      <div className="page-shell">
        <Header />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <Header />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-6 space-y-5">
        {/* Welcome + Logout */}
        <section className="surface-card-soft rounded-[30px] px-6 py-6 md:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <span className="mb-3 inline-flex rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                {t('dashboard.badgeLabel')}
              </span>
              <p className="text-sm text-gray-500">{t('dashboard.welcomeBack')},</p>
              <h1 className="text-2xl font-bold text-gray-900 md:text-4xl">
                {user.name}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                {t('dashboard.description')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => logout()}
            className="flex items-center gap-2 self-start rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-100"
            >
              <LogOut className="w-4 h-4" />
              {t('header.logout')}
            </button>
          </div>
        </section>

        {/* Quick-start exam cards */}
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.startPracticing')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { icon: BookOpen, label: t('header.sections.reading'), section: 'Lezen', color: 'text-primary', bg: 'bg-white hover:bg-slate-50', border: 'border-primary/15' },
              { icon: Headphones, label: t('header.sections.listening'), section: 'Luisteren', color: 'text-primary', bg: 'bg-white hover:bg-slate-50', border: 'border-primary/15' },
              { icon: PenSquare, label: t('header.sections.writing'), section: 'Schrijven', color: 'text-primary', bg: 'bg-white hover:bg-slate-50', border: 'border-primary/15' },
              { icon: Mic, label: t('header.sections.speaking'), section: 'Spreken', color: 'text-primary', bg: 'bg-white hover:bg-slate-50', border: 'border-primary/15' },
              { icon: Landmark, label: t('header.sections.knm'), section: 'KNM', color: 'text-primary', bg: 'bg-white hover:bg-slate-50', border: 'border-primary/15' },
            ].map(({ icon: Icon, label, section, color, bg, border }) => (
              <button
                key={section}
                onClick={() => navigate(`/exam-selection?section=${section}`)}
                className={`${bg} ${border} group flex flex-col items-center gap-2 rounded-[24px] border p-4 transition-all hover:-translate-y-0.5 hover:shadow-surface`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <span className="font-medium text-sm text-gray-900">{label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* User Info + Subscription row */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* User Info */}
          <div className="surface-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900">{t('dashboard.profile.title')}</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('dashboard.profile.name')}</span>
                <span className="font-medium text-gray-900">{user.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('dashboard.profile.email')}</span>
                <span className="font-medium text-gray-900 truncate ml-4">{user.email}</span>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="surface-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900">{t('dashboard.subscriptionCard.title')}</h3>
            </div>
            {loadingSub ? (
              <p className="text-gray-400">{t('dashboard.subscriptionCard.loading')}</p>
            ) : subscription?.status === 'active' ? (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('dashboard.subscriptionCard.plan')}</span>
                  <span className="font-medium text-gray-900">{getPlanLabel(subscription.plan, t)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('dashboard.subscriptionCard.status')}</span>
                  <span className="inline-flex items-center gap-1.5 text-green-600 font-medium">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    {t('dashboard.subscriptionCard.active')}
                  </span>
                </div>
                {subscription.currentPeriodEnd && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('dashboard.subscriptionCard.renewsOn')}</span>
                    <span className="font-medium text-gray-900">{new Date(subscription.currentPeriodEnd).toLocaleDateString('nl-NL')}</span>
                  </div>
                )}
                <Link to="/dashboard/subscription" className="mt-1 block text-sm text-primary hover:underline">
                  {t('dashboard.subscriptionCard.manage')}
                </Link>
              </div>
            ) : subscription?.passActive ? (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('dashboard.subscriptionCard.plan')}</span>
                  <span className="font-medium text-gray-900">
                    {(() => {
                      const tier = getPassTier(subscription.passTier);
                      return tier
                        ? t(`${tier.i18nKey}.name`)
                        : t('dashboard.subscriptionCard.passPlan');
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('dashboard.subscriptionCard.status')}</span>
                  <span className="inline-flex items-center gap-1.5 text-green-600 font-medium">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    {t('dashboard.subscriptionCard.active')}
                  </span>
                </div>
                {subscription.passExpiresAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('dashboard.subscriptionCard.expiresOn')}</span>
                    <span className="font-medium text-gray-900">
                      {new Date(subscription.passExpiresAt).toLocaleDateString('nl-NL')}
                    </span>
                  </div>
                )}
                <Link to="/pricing" className="mt-1 block text-sm text-primary hover:underline">
                  {t('dashboard.subscriptionCard.viewPasses')}
                </Link>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-gray-500">{t('dashboard.subscriptionCard.noActive')}</p>
                <Link
                  to="/pricing"
                  className="rounded-lg bg-primary px-4 py-2 text-sm text-white transition-colors hover:brightness-110"
                >
                  {t(isPassesModel ? 'dashboard.subscriptionCard.viewPasses' : 'dashboard.subscriptionCard.viewPlans')}
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Statistics and progress - paid access only */}
        {emailVerificationBlocked ? (
          <EmailVerificationPanel />
        ) : loadingSub || (hasAccess && loadingResults) ? (
          <div className="surface-card flex items-center justify-center p-4">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : !hasAccess ? (
          <SubscriptionLockedState />
        ) : (
          <>
            <StatsCards stats={statsForCards} />
            <ProgressChart />
            <RecentExamsTable exams={recentExams} />
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default DashboardPage;
